/**
 * Die Kopier-Abfrage: was aus der Vorlage in die Kopie mitgeht.
 *
 * ## Warum eine eigene Datei und keine Zeilen im Dialog
 *
 * Dieselbe Begründung wie bei `anhaenge.ts` und `booking.ts`: Die Tests laufen
 * ohne Browser, und was in einer `.svelte`-Datei steht, ist für sie
 * unerreichbar. Hier kommt ein zweiter Grund dazu — beides, was hier gerechnet
 * wird, kann **still** falsch sein:
 *
 * 1. **Ein Body, der ein Feld verschluckt.** Wer die Belege ankreuzt und ein
 *    `belege: true` nicht mitschickt, bekommt eine vollkommen plausible Kopie
 *    zurück — nur ohne die Belege. Aufgefallen wäre das an der Rezeption.
 * 2. **Eine Meldung, die zu viel behauptet.** „2 Belege mitkopiert" über einer
 *    Kopie, der einer fehlt, ist keine Ungenauigkeit, sondern eine Auskunft
 *    über Dateien, die es nicht gibt.
 *
 * ## Eine Richtung für alle vier Schalter
 *
 * Jedes Feld heißt „mitnehmen": angehakt heißt mitkommen. Eine gemischte Liste
 * aus „mitnehmen" und „weglassen" wäre an genau einem Tag verständlich — dem,
 * an dem man sie schreibt. Die Vorgaben sind dieselben wie im Server
 * (`KopierOptionen` in `main.py`), Feld für Feld, und sie sind das Verhalten
 * von vor dem Dialog.
 *
 * ## Warum die Belege als einzige ausgeschaltet vorstehen
 *
 * Sie sind die einzige Option, die Platz kostet: Die PDFs werden wirklich ein
 * zweites Mal abgelegt und zählen ein zweites Mal auf das Kontingent. Eine
 * Vorgabe, die stillschweigend Speicher verbraucht, wäre die falsche.
 */

import { anzahl, wort } from './planner/sprache';

/** Der Stand der vier Ankreuzfelder im Dialog. */
export interface Kopierwahl {
  belege: boolean;
  erreicht: boolean;
  tagesplan_haken: boolean;
  quartier_status: boolean;
}

/**
 * Der Body von `POST /trips/{id}/duplicate` — jedes Feld freiwillig.
 *
 * Steht hier und nicht in `api.ts`, weil er hier **entsteht**; `api.ts` holt
 * ihn sich als Typ, so wie es `TarifDraht` aus dem Ladeplaner holt. Der Server
 * verbietet unbekannte Felder (`extra="forbid"`), ein Tippfehler wird also
 * nicht verschluckt, sondern mit 422 beantwortet.
 */
export interface Kopieroptionen {
  belege?: boolean;
  erreicht?: boolean;
  tagesplan_haken?: boolean;
  quartier_status?: boolean;
}

/**
 * Die zwei Zahlen, die **nur** bei mitkopierten Belegen dabeistehen.
 *
 * Sie hängen flach an der neuen Reise und nicht in einem eigenen Umschlag —
 * so hat der Server es gebaut, damit die Antwort für jeden Aufrufer die Reise
 * bleibt, die sie immer war.
 */
export interface Belegbilanz {
  belege_kopiert?: number;
  belege_uebersprungen?: number;
}

/** Was der Server tut, wenn niemand etwas sagt. Wort für Wort seine Vorgaben. */
export const KOPIER_VORGABE: Kopierwahl = {
  belege: false,
  erreicht: true,
  tagesplan_haken: true,
  quartier_status: true
};

/** Ein Ankreuzfeld des Dialogs — Beschriftung und der Halbsatz darunter. */
export interface Kopierfeld {
  schluessel: keyof Kopierwahl;
  beschriftung: string;
  /** Warum man das ankreuzt oder eben nicht. Ein Satz, keine Feldbeschreibung. */
  erklaerung: string;
}

/**
 * Die vier Fragen, in der Reihenfolge des Dialogs.
 *
 * Die Belege stehen oben, weil sie als einzige etwas kosten und als einzige
 * ausgeschaltet vorstehen — die Frage, bei der jemand wirklich etwas
 * entscheidet, gehört nicht ans Ende einer Liste, die man abnickt.
 *
 * Beschriftungen für Menschen und nicht die Namen aus dem Vertrag: Wer
 * `quartier_status` liest, weiß nicht, ob damit sein Telefonat gemeint ist.
 */
export const KOPIERFELDER: readonly Kopierfeld[] = [
  {
    schluessel: 'belege',
    beschriftung: 'Buchungsbelege mitkopieren',
    erklaerung:
      'Jedes PDF wird ein zweites Mal abgelegt und zählt ein zweites Mal auf deinen Speicher.'
  },
  {
    schluessel: 'erreicht',
    beschriftung: 'Erreicht-Stände übernehmen',
    erklaerung: 'Welche Stationen schon abgehakt sind. Für eine Variante ab heute: abwählen.'
  },
  {
    schluessel: 'tagesplan_haken',
    beschriftung: 'Tagesplan-Haken übernehmen',
    erklaerung:
      'Nur die Häkchen. Die eingehängten Tagespläne und ihre Fußwege kommen in jedem Fall mit.'
  },
  {
    schluessel: 'quartier_status',
    beschriftung: 'Anfrage-Stand der Ausweichquartiere übernehmen',
    erklaerung: 'Angefragt, zugesagt, abgesagt — der Verlauf des Gesprächs mit dem Platz.'
  }
];

/** Steht alles auf Vorgabe? Dann ist der Aufruf der von vor dem Dialog. */
export function istVorgabe(wahl: Kopierwahl): boolean {
  return KOPIERFELDER.every((f) => wahl[f.schluessel] === KOPIER_VORGABE[f.schluessel]);
}

/**
 * Der Body zur Wahl — oder `undefined`, wenn keiner gebraucht wird.
 *
 * Steht alles auf Vorgabe, geht der Aufruf **ohne** Body hinaus. Nicht aus
 * Sparsamkeit: Es ist dann Zeichen für Zeichen derselbe Aufruf, den dieser
 * Client seit jeher schickt, und damit der einzige Fall, in dem ein Server
 * ohne `KopierOptionen` nicht mit 422 antworten könnte.
 *
 * Ist etwas abgewählt, gehen **alle vier** Felder mit und nicht nur das
 * abweichende. Ein halber Body verließe sich darauf, dass die Vorgaben dort
 * drüben dieselben bleiben — und genau diese Annahme fällt niemandem auf, wenn
 * sie eines Tages nicht mehr stimmt.
 */
export function kopierbody(wahl: Kopierwahl): Kopieroptionen | undefined {
  if (istVorgabe(wahl)) return undefined;
  return {
    belege: wahl.belege,
    erreicht: wahl.erreicht,
    tagesplan_haken: wahl.tagesplan_haken,
    quartier_status: wahl.quartier_status
  };
}

/** Eine Zahl aus der Antwort — oder `null`, wenn dort keine steht. */
function zahl(roh: unknown): number | null {
  return typeof roh === 'number' && Number.isFinite(roh) && roh >= 0 ? Math.trunc(roh) : null;
}

/**
 * Was mit den Belegen passiert ist — oder `null`, wenn keine bestellt waren.
 *
 * Die beiden Zahlen stehen nur bei `belege: true` in der Antwort. Fehlen sie,
 * gibt es nichts zu melden, und eine Kopie ohne Meldung ist die ruhigere.
 *
 * Übersprungen wird ein Verweis, dessen Datei auf der Platte fehlt. Das
 * ausdrücklich zu sagen ist der ganze Zweck der zweiten Zahl: Eine Kopie, der
 * ein Beleg fehlt, sieht vollständig aus — bis jemand an der Rezeption steht.
 */
export function kopiermeldung(antwort: Belegbilanz | null | undefined): string | null {
  const kopiert = zahl(antwort?.belege_kopiert);
  const uebersprungen = zahl(antwort?.belege_uebersprungen);
  if (kopiert === null && uebersprungen === null) return null;

  const k = kopiert ?? 0;
  const u = uebersprungen ?? 0;
  // Angehakt und trotzdem nichts zu tun: An der Vorlage hing kein einziger
  // Beleg. Das ist kein Fehler, aber wer es angekreuzt hat, wartet auf eine
  // Antwort — und Schweigen liest sich hier wie ein Fehlschlag.
  if (k === 0 && u === 0) return 'An dieser Reise hing kein Beleg. Die Kopie steht trotzdem.';

  const saetze: string[] = [];
  if (k > 0) saetze.push(`${anzahl(k, 'Beleg', 'Belege')} mitkopiert.`);
  if (u > 0) {
    saetze.push(
      `${anzahl(u, 'Beleg', 'Belege')} ${wort(u, 'fehlte', 'fehlten')} auf dem Server ` +
        `und ${wort(u, 'wurde', 'wurden')} übersprungen.`
    );
  }
  return saetze.join(' ');
}
